import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "../styles/ReportsPage.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [alertData, setAlertData] = useState([]);
  const [speedData, setSpeedData] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(false);

  const COLORS = ["#2563eb", "#f59e0b", "#ef4444", "#16a34a", "#0ea5e9"];

  // --- Load vehicle list ---
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/vehicles`);
        const data = await res.json();
        setVehicles(data);
      } catch (err) {
        console.error("Failed to fetch vehicles:", err);
      }
    };
    fetchVehicles();
  }, []);

  // --- Fetch report data ---
  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vehicleId && vehicleId !== "all") params.append("vehicleId", vehicleId);
      if (start) params.append("start", start);
      if (end) params.append("end", end);

      const res = await fetch(`${API_URL}/v1/reports/summary?${params.toString()}`);
      const data = await res.json();
      setStats(data);
      setAlertData(data.alertsByType || []);
      setSpeedData(data.avgSpeedOverTime || []);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  // --- Server-side export handlers ---
  const handleServerExport = async (format) => {
    try {
      const params = new URLSearchParams();
      if (vehicleId && vehicleId !== "all") params.append("vehicleId", vehicleId);
      if (start) params.append("start", start);
      if (end) params.append("end", end);

      const url = `${API_URL}/v1/reports/export.${format}?${params.toString()}`;
      const res = await fetch(url);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Fleet_Report_${vehicleId === "all" ? "AllFleet" : "Vehicle_" + vehicleId}.${format}`;
      link.click();
    } catch (err) {
      console.error(`Failed to export ${format}:`, err);
    }
  };

  return (
    <div className="reports-layout bg-gray-50 text-gray-900">
      {/* ===== Header ===== */}
      <div className="reports-header border-b border-gray-200 p-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Fleet Reports And Analytics</h1>
          <p className="text-sm text-gray-500">
            Generate insights for{" "}
            <b>{vehicleId === "all" ? "entire fleet" : `Vehicle ${vehicleId}`}</b>
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <select
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
          >
            <option value="all">All Vehicles</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.registrationNumber || `Vehicle ${v.id}`}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          />

          <button
            onClick={fetchReport}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-sm"
          >
            {loading ? "Loading..." : "Generate"}
          </button>

          <button
            onClick={() => handleServerExport("xlsx")}
            className="bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 text-sm"
          >
            Excel
          </button>

          <button
            onClick={() => handleServerExport("pdf")}
            className="bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 text-sm"
          >
            PDF
          </button>
        </div>
      </div>

      {/* ===== Summary Cards ===== */}
      {stats && (
        <div className="summary-grid p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card">
            <h3>Total Vehicles</h3>
            <p>{stats.overview?.totalVehicles ?? "--"}</p>
          </div>
          <div className="card">
            <h3>Active Today</h3>
            <p>{stats.overview?.activeVehicles ?? "--"}</p>
          </div>
          <div className="card">
            <h3>Distance Covered (km)</h3>
            <p>{stats.overview?.totalDistance ?? "--"}</p>
          </div>
          <div className="card">
            <h3>Fuel Used (L)</h3>
            <p>{stats.overview?.fuelUsed ?? "--"}</p>
          </div>
          <div className="card">
            <h3>Fleet Efficiency (km/L)</h3>
            <p>{stats.overview?.fleetEfficiencyKmPerL ?? "N/A"}</p>
          </div>
        </div>
      )}

      {/* ===== Charts ===== */}
      <div className="charts-grid grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
        <div className="chart-card">
          <h3>Alert Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={alertData}
                dataKey="count"
                nameKey="type"
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                label
              >
                {alertData.map((entry, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Average Speed Over Time</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={speedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="avgSpeed" stroke="#2563eb" name="Speed (km/h)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card md:col-span-2">
          <h3>Daily Alert Activity</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats?.overview?.alertsOverTime || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="alerts" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ===== Per-Vehicle Statistics ===== */}
      {stats?.perVehicle?.length > 0 && (
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-2">Per-Vehicle Statistics</h3>
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left">Vehicle</th>
                  <th className="px-3 py-2 text-left">Distance (km)</th>
                  <th className="px-3 py-2 text-left">Fuel (L)</th>
                  <th className="px-3 py-2 text-left">Km/L</th>
                  <th className="px-3 py-2 text-left">Ignition ON (h)</th>
                  <th className="px-3 py-2 text-left">Downtime (h)</th>
                </tr>
              </thead>
              <tbody>
                {stats.perVehicle.map((v) => (
                  <tr key={v.vehicleId} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-1.5">{v.vehicleId}</td>
                    <td className="px-3 py-1.5">{v.distanceKm}</td>
                    <td className="px-3 py-1.5">{v.fuelL}</td>
                    <td className="px-3 py-1.5">{v.efficiencyKmPerL ?? "N/A"}</td>
                    <td className="px-3 py-1.5">{v.ignitionOnHours}</td>
                    <td className="px-3 py-1.5">{v.downtimeHours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
